import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback } from 'react';

import { getLoginRequest, getTokenRequest } from './msalConfig';

/** Shape returned by {@link useAuth}. */
export interface UseAuth {
  /** True once an account is present in the MSAL cache. */
  isAuthenticated: boolean;
  /** The active account, or `null` when signed out. */
  account: AccountInfo | null;
  /** Display name for the signed-in user (falls back to username). */
  displayName: string | null;
  /** Begin interactive sign-in (redirect flow). */
  login: () => Promise<void>;
  /** Sign out (redirect flow). */
  logout: () => Promise<void>;
  /**
   * Acquire an access token for the WarehouseLogix API scope.
   * Tries silent acquisition first; on an interaction-required error it kicks
   * off a redirect (which navigates away) rather than looping silently.
   */
  acquireToken: () => Promise<string>;
}

/**
 * Thin, typed wrapper over the MSAL React hooks that centralizes login/logout
 * and token acquisition for the WarehouseLogix API scope.
 */
export function useAuth(): UseAuth {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] ?? null;

  const login = useCallback(async (): Promise<void> => {
    await instance.loginRedirect(getLoginRequest());
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    await instance.logoutRedirect();
  }, [instance]);

  const acquireToken = useCallback(async (): Promise<string> => {
    const active = instance.getActiveAccount() ?? accounts[0];
    if (active === undefined) {
      // No account at all — send the user through interactive sign-in.
      await instance.acquireTokenRedirect(getLoginRequest());
      // acquireTokenRedirect navigates away; this line is not reached.
      throw new Error('Redirecting to sign-in.');
    }
    try {
      const result = await instance.acquireTokenSilent({ ...getTokenRequest(), account: active });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...getTokenRequest(), account: active });
        throw new Error('Redirecting to re-authenticate.');
      }
      throw error;
    }
  }, [instance, accounts]);

  const displayName = account?.name ?? account?.username ?? null;

  return { isAuthenticated, account, displayName, login, logout, acquireToken };
}
